import AppProps from "./AppProps";
import {FeatureCollection} from "geojson";

const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { ...options, headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response;
};

/**
 * @param locale {string & 'ru' | 'en' }
 * @return {Promise<Product[]>}
 */
export const fetchProducts = async (locale = 'ru') => {
    const res = await fetch(`${AppProps.spring_server_url}/products/all?locale=${locale}`);
    if (!res.ok) {
        throw new Error(`Ошибка загрузки: ${res.status}`);
    }
    return await res.json();
};

/**
 * @param locale {string & 'ru' | 'en' }
 * @return {Promise<Product[]>}
 */
export const getUserProductsList = async (locale = 'ru') => {
    try {
        const url = `${AppProps.spring_server_url}/products/of-user?locale=${locale}`;
        const response = await fetchWithAuth(url, { method: 'GET' });
        return await response.json();
    } catch (error) {
        console.error("Error fetching products:", error);
        throw error;
    }
};

/**
 * @param objectId {number}
 * @param year {number}
 * @param month {number}
 * @return {Promise<any>}
 */
export async function getDataOfPickedArea(objectId, year, month) {
    const url = new URL(`${AppProps.spring_server_url}/map/values/values-of-picked-area`);
    url.searchParams.append("object-id", objectId.toString());
    url.searchParams.append("date", getISODateFromYearAndMonth(year, month));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return await response.json();
}

/**
 * Получает значения фичи для объектов выбранного региона
 * @param featureId {number}
 * @param regionId {number}
 * @param yearOf {number}
 * @param month {number}
 * @param size {number}
 * @param useSmooth {boolean}
 * @return {{number: number}}
 */
export async function getValuesOfFeatureByYear(
    featureId,
    regionId,
    yearOf,
    month,
    size,
    useSmooth = true
) {
    const url = new URL(`${AppProps.spring_server_url}/map/values/of-region`);
    url.searchParams.append('feature', featureId.toString());
    url.searchParams.append('region', regionId.toString());
    url.searchParams.append('date', getISODateFromYearAndMonth(yearOf, month));
    url.searchParams.append('size', size.toString());
    url.searchParams.append('smooth', useSmooth.toString());
    const response = await fetchWithAuth(url, { method: 'GET' });
    return await response.json();
}

/**
 * Получает GeoJSON с объектами на карте
 * @param regionId {number}
 * @param hexSize {number || null}
 * @return {Promise<FeatureCollection>}
 */
export async function getProductMapObjects(regionId, hexSize = null) {
    if (regionId == null) throw new Error("regionId is required");
    const url = new URL(`${AppProps.spring_server_url}/map/geojson/objects/of-region`);
    url.searchParams.append('id', regionId.toString());
    hexSize && url.searchParams.append('size', hexSize.toString());
    const response = await fetchWithAuth(url, { method: 'GET' });
    return await response.json();
}

/**
 * Возвращает массив дат, доступных для фичи
 * @param id {number}
 * @param region {number}
 * @return {Promise<Date[]>}
 */
export async function getArrayOfDatesOfFeature(id, region) {
    const url = new URL(`${AppProps.spring_server_url}/map/values/dates-of-feature`);
    url.searchParams.append('feature', id.toString());
    url.searchParams.append('region', region.toString());
    const response = await fetchWithAuth(url, {method: 'GET'});
    /** @type {string[]} */ let dateStrings = await response.json();
    return dateStrings.map(dateString => new Date(dateString));
}

/**
 * Возвращает массив годов для фичи
 * @param featureId {number}
 * @param regionId {number}
 * @return {Promise<number[]>}
 */
export async function getArrayOfYearsOfFeature(featureId, regionId) {
    const dates = await getArrayOfDatesOfFeature(featureId, regionId);
    return dates.map(date => date.getFullYear()).sort((a, b) => a - b);
}

/**
 * Получает допустимые размеры объектов региона
 * @param regionId {number}
 * @return {Promise<number[]>}
 */
export async function getHexSizes(regionId) {
    console.log('getHexSizes called with regionId:', regionId)
    if (!regionId) throw new Error("regionId is required");
    const url = new URL(`${AppProps.spring_server_url}/map/objects-of-regions/sizes-of-region`)
    console.log('Request URL:', url.toString());
    url.searchParams.append('id', regionId.toString());
    const response = await fetchWithAuth(url, { method: 'GET' });
    const sizes = await response.json();
    if (!Array.isArray(sizes) || sizes.length === 0) throw new Error("Нет доступных размеров гексагонов");
    return sizes.sort((a, b) => a - b);
}

/**
 * @param featureId
 * @param regionId
 * @return {Promise<{number : number[]}>}
 */
export async function getYearsAndMonthsOfFeature(featureId, regionId) {
    const dates = await getArrayOfDatesOfFeature(featureId, regionId);
    const collection = {};
    dates.forEach(date => collection[date.getFullYear()] = []);
    dates.forEach(date => {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        if (!collection[year].includes(month)) {
            collection[year].push(month);
        }
    });
    Object.keys(collection).forEach(year => {
        collection[year].sort((a, b) => a - b);
    });
    return collection;
}

/**
 * @param year {number}
 * @param month {number}
 * @return {string}
 */
function getISODateFromYearAndMonth(year, month = 1) {
    const formattedMonth = String(month).padStart(2, '0');
    return `${year}-${formattedMonth}-01`;
}

/**
 * 
 * @param {Date} yearOf
 * @param {number[]} mapObjects 
 * @param {number} [monthOf=1] 
 * @return {Promise<{number[]}>}
 */
export async function fetchValuesOfSelectedObjects(yearOf, mapObjects, monthOf = 1) {
    const request = new URL(`${AppProps.spring_server_url}/map/values/of-selected-objects`);
    const response = await fetch(
        request, 
        { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                "date_of": getISODateFromYearAndMonth(yearOf, monthOf), 
                "objects": mapObjects 
            })
        });
    return await response.json();
}
